import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import AuthShell from '../../components/auth/AuthShell';
import { slides, PRODUCT_LABEL } from '../../components/auth/slides';

export default function Login() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, org } = useAuth();

  // Once auth resolves and we have an org, leave the login page.
  useEffect(() => {
    if (!authLoading && isAuthenticated && org) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, isAuthenticated, org, navigate]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Don't navigate yet — let AuthContext load org/profile first.
      // The useEffect above watches isAuthenticated && !loading and navigates then.
    } catch (err) {
      console.error(err);
      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('Incorrect email or password');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts — please try again later');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled');
          break;
        default:
          setError('Failed to sign in — please try again');
      }
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  const label = {
    display: 'block',
    fontSize: 11,
    color: 'var(--color-text-secondary)',
    marginBottom: 5,
  };

  const field = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    fontSize: 14,
    fontFamily: 'inherit',
    border: '0.5px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-md)',
  };

  return (
    <AuthShell productLabel={PRODUCT_LABEL} slides={slides}>
      <div style={{ fontSize: 22, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>
        Sign in
      </div>
      <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 28 }}>
        Welcome back.
      </div>

      {error && (
        <div style={{
          background: 'var(--color-danger-light)',
          border: '0.5px solid var(--color-danger)',
          borderRadius: 'var(--radius-md)',
          padding: '10px 14px',
          fontSize: 13,
          color: 'var(--color-danger)',
          marginBottom: 16,
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <label style={label}>Work email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="email"
          className="agw-auth-input"
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={label}>Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="current-password"
          className="agw-auth-input"
        />
      </div>

      <button
        onClick={handleLogin}
        disabled={loading}
        style={{
          width: '100%',
          padding: '11px',
          background: 'var(--color-brand)',
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          fontSize: 14,
          fontFamily: 'inherit',
          cursor: loading ? 'default' : 'pointer',
          opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? 'Signing in…' : 'Sign in'}
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18, fontSize: 12 }}>
        <Link to="/reset-password" style={{ color: 'var(--color-text-tertiary)', textDecoration: 'none' }}>
          Forgotten your password?
        </Link>
        <Link to="/signup" style={{ color: 'var(--color-brand)', textDecoration: 'none' }}>
          Create an account
        </Link>
      </div>
    </AuthShell>
  );
}
